import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import theme, { palette } from "~frontend/theme";

// Format a stored sub-unit value as a dollar string with `decimalPlaces`
// precision, then trim trailing zeros (so `1500` at 5dp shows as "0.015"
// rather than "0.01500", and `100000` at 5dp shows as "1" rather than "1.00000").
function formatStoredAsDollar(value: number, decimalPlaces: number): string {
  const subUnitsPerDollar = Math.pow(10, decimalPlaces);
  const fixed = (value / subUnitsPerDollar).toFixed(decimalPlaces);
  return fixed.replace(/\.?0+$/, "") || "0";
}

export function RhfCurrencyField<T extends FieldValues>(
  props: Omit<TextFieldProps, "value" | "onChange" | "name"> &
    UseControllerProps<T> & {
      optional?: boolean;
      bottomLabel?: string;
      adornmentOffset?: number;
      isSlim?: boolean;
      // How many decimal digits the field's stored value represents per dollar.
      // Default 2 (cents). Use 5 for millicent precision so values like
      // 1500 millicents display as "0.015" without rounding.
      decimalPlaces?: number;
    }
): ReactElement {
  const { field, fieldState } = useController(props);

  const { optional, bottomLabel, isSlim, decimalPlaces = 2, ...rest } = props;
  const subUnitsPerDollar = Math.pow(10, decimalPlaces);

  // Render the stored value as a display string. Use an explicit numeric
  // check (not a truthy check) so the literal `0` renders as "0" and isn't
  // mistaken for an empty input — which would silently mask a stored value
  // and dodge required-field validation.
  const formatFieldValue = (val: unknown): string =>
    typeof val === "number" ? formatStoredAsDollar(val, decimalPlaces) : "";

  const [displayValue, setDisplayValue] = useState(() =>
    formatFieldValue(field.value)
  );

  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDisplayValue(formatFieldValue(field.value));
    }
    // formatFieldValue depends on decimalPlaces (already a dep) and is
    // recreated each render — safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, decimalPlaces]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setDisplayValue(inputValue);

    if (inputValue === "") {
      field.onChange("");
      return;
    }

    const dollarAmount = parseFloat(inputValue);
    if (isNaN(dollarAmount)) {
      // Don't silently coerce non-numeric input to 0 — that would let the
      // form pass validation with a value the user didn't actually enter.
      // Leave the form value as the raw (non-numeric) input so the schema
      // surfaces a "Required" error.
      field.onChange(inputValue);
    } else {
      field.onChange(Math.round(dollarAmount * subUnitsPerDollar));
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;

    if (displayValue && displayValue !== "") {
      const dollarAmount = parseFloat(displayValue);
      if (isNaN(dollarAmount)) {
        setDisplayValue("");
      } else {
        setDisplayValue(
          formatStoredAsDollar(
            Math.round(dollarAmount * subUnitsPerDollar),
            decimalPlaces
          )
        );
      }
    }

    field.onBlur();
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  return (
    <MuiTextField
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      value={displayValue}
      name={field.name}
      inputRef={field.ref}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error ? fieldState.error.message : bottomLabel}
      {...(rest as TextFieldProps)}
      variant={isSlim ? "standard" : rest.variant}
      sx={
        isSlim
          ? [
              {
                "& .MuiInput-underline:before": {
                  borderBottomColor: palette.lightGray,
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: palette.lightGray,
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: palette.midGray,
                },
                ...(rest.sx as object),
              },
              rest.sx as object,
            ]
          : rest.sx
      }
      label={
        isSlim ? undefined : (
          <FlexBox alignItems="center" gap={1}>
            {props.label}
            {optional ? <Typography> (optional)</Typography> : ""}
          </FlexBox>
        )
      }
      slotProps={{
        htmlInput: {
          ...rest.inputProps,
          // Add a small left padding in slim mode so the typed value doesn't
          // sit flush against the `$` adornment.
          sx: isSlim ? { fontSize: "12px", py: 0, pl: 1.25 } : undefined,
        },
        inputLabel: isSlim
          ? {
              sx: { maxWidth: `calc(140% - ${theme.spacing(4)})` },
              shrink: true,
            }
          : {
              sx: {
                width: "133%",
                maxWidth: `calc(140% - ${theme.spacing(4)})`,
              },
              shrink: true,
              ...(props.InputLabelProps || props.slotProps?.inputLabel || {}),
            },
        input: {
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                marginLeft:
                  props.adornmentOffset !== undefined
                    ? props.adornmentOffset
                    : 1,
                marginRight:
                  props.adornmentOffset !== undefined
                    ? props.adornmentOffset * -1
                    : -1,
                marginTop: 0,
              }}
            >
              <Typography
                display="inline"
                sx={{ fontSize: isSlim ? "12px" : "1.0em" }}
              >
                $
              </Typography>
            </InputAdornment>
          ),
          ...(props.InputProps || props.slotProps?.input || {}),
        },
      }}
    />
  );
}
